import { motion } from 'motion/react';
import { PropsWithChildren, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ANIMATION_CONFIG } from '../../../constants/animationConfig';
import { COLORS, device } from '../../../constants';
import { useLocation } from 'react-router-dom';

interface MotionHomeHeaderBottomLogoProps extends PropsWithChildren {
  className?: string;
  size: number;
  lgSize: number;
  bsmSize: number;
  smSize: number;
  leftOffset?: number;
}

export const MotionHomeHeaderBottomLogo = ({
  className,
  children,
  size,
  lgSize,
  bsmSize,
  smSize,
  leftOffset,
  ...props
}: MotionHomeHeaderBottomLogoProps) => {
  const ref = useRef<HTMLDivElement | null>(null);

  return (
    <MotionHomeHeaderBottomLogoWrapper
      {...props}
      ref={ref}
      className={className}
      $size={size}
      $lgSize={lgSize}
      $bsmSize={bsmSize}
      $smSize={smSize}
      $leftOffset={leftOffset || 0}
      initial={{
        opacity: 0,
        rotate: ANIMATION_CONFIG.homeHeader.bottomLogo.rotate,
      }}
      animate={{ opacity: 1, rotate: 0 }}
      transition={{
        duration: ANIMATION_CONFIG.homeHeader.bottomLogo.duration,
        delay: ANIMATION_CONFIG.homeHeader.bottomLogo.delay,
        ease: 'easeIn',
        type: 'spring',
      }}
    >
      {children}
    </MotionHomeHeaderBottomLogoWrapper>
  );
};

const MotionHomeHeaderBottomLogoWrapper = styled(motion.div)<{
  $size: number;
  $lgSize: number;
  $bsmSize: number;
  $smSize: number;
  $leftOffset: number;
}>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 2px dashed ${COLORS.ACTIVE_ITEM};

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: ${({ $leftOffset }) => $leftOffset}px;
    width: ${({ $size }) => $size}px;
    height: ${({ $size }) => $size}px;
    border-radius: 50%;
    border: 1.5px dashed ${COLORS.ACTIVE_ITEM};
    opacity: 0;
    transform: scale(1);
    pointer-events: none;
    z-index: 0;
    animation: pulse-ring-header-main infinite;
    animation-duration: ${ANIMATION_CONFIG.keyFrames.mainHeader.duration}s;
    animation-delay: ${ANIMATION_CONFIG.keyFrames.mainHeader.delay}s;
    animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1);
  }

  @media screen and (${device.lg}) {
    width: ${({ $lgSize }) => $lgSize}px;
    height: ${({ $lgSize }) => $lgSize}px;

    &::after {
      width: ${({ $lgSize }) => $lgSize}px;
      height: ${({ $lgSize }) => $lgSize}px;
      left: ${({ $leftOffset }) => $leftOffset / 2}px;
    }
  }
  @media screen and (${device.bsm}) {
    width: ${({ $bsmSize }) => $bsmSize}px;
    height: ${({ $bsmSize }) => $bsmSize}px;

    &::after {
      width: ${({ $bsmSize }) => $bsmSize}px;
      height: ${({ $bsmSize }) => $bsmSize}px;
      left: ${({ $leftOffset }) => $leftOffset / 3}px;
    }
  }
  @media screen and (${device.sm}) {
    width: ${({ $smSize }) => $smSize}px;
    height: ${({ $smSize }) => $smSize}px;

    &::after {
      width: ${({ $smSize }) => $smSize}px;
      height: ${({ $smSize }) => $smSize}px;
      left: ${({ $leftOffset }) => $leftOffset / 4}px;
    }
  }

  @keyframes pulse-ring-header-main {
    0% {
      transform: scale(1);
      opacity: 0;
    }
    8% {
      transform: scale(1.3);
      opacity: 0.7;
    }
    17% {
      transform: scale(1.3);
      opacity: 0.7;
    }
    25% {
      transform: scale(1.5);
      opacity: 0.5;
    }
    33% {
      transform: scale(1.5);
      opacity: 0.5;
    }
    42% {
      transform: scale(1.8);
      opacity: 0.3;
    }
    50% {
      transform: scale(1.8);
      opacity: 0.3;
    }
    58% {
      transform: scale(2);
      opacity: 0.1;
    }
    67% {
      transform: scale(2);
      opacity: 0.1;
    }
    83% {
      transform: scale(3);
      opacity: 0;
    }
    75% {
      transform: scale(3);
      opacity: 0;
    }
    92% {
      transform: scale(1);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 0;
    }
  }
`;
